import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { getReportOverride, saveReportOverride } from "../axios/axiosFunctions";

// Per-team admin curation overlay for the TIM / E-TIM report. Provides the
// edited text / verbatim state to every report component (online + the PDF gets
// the same object as props) and, for admins, an inline edit mode that buffers
// changes until Save. Originals are never mutated.
export const ReportEditContext = createContext({
  canEdit: false,
  editing: false,
  setEditing: () => null,
  loaded: false,
  dirty: false,
  saving: false,
  overlay: { texts: {}, hiddenVerbatim: [], editedVerbatim: {} },
  getText: (_k, def) => def,
  setText: () => null,
  isHidden: () => false,
  toggleHidden: () => null,
  getVerbatim: (_id, def) => def,
  setVerbatim: () => null,
  save: async () => false,
  discard: () => null,
});

export const emptyOverlay = () => ({
  texts: {},
  hiddenVerbatim: [],
  editedVerbatim: {},
});

// Apply the overlay to a list of verbatim answers (drop hidden, swap reworded).
// Idempotent: safe to run on an already-curated list from the server.
export const curateVerbatim = (list, overlay) => {
  if (!overlay) return list || [];
  const hidden = new Set((overlay.hiddenVerbatim || []).map(String));
  const edited = overlay.editedVerbatim || {};
  return (list || [])
    .filter((a) => !hidden.has(String(a._id)))
    .map((a) => {
      const ed = edited[String(a._id)];
      return ed != null && ed !== "" ? { ...a, answer: ed } : a;
    });
};

// Owns the overlay state + edit-mode buffer. Returned value is fed straight into
// ReportEditContext.Provider; ReportAssessment also reads `.overlay` to curate
// the PDF feed (so an admin's exported PDF matches the curated client view).
export const useReportEditValue = (groupId, canEdit) => {
  const [editing, setEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // working buffer + last-saved snapshot (for discard)
  const [overlay, setOverlay] = useState(emptyOverlay());
  const [saved, setSaved] = useState(emptyOverlay());

  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    (async () => {
      const ov = await getReportOverride(groupId);
      if (cancelled) return;
      const normalized = {
        texts: ov?.texts || {},
        hiddenVerbatim: ov?.hiddenVerbatim || [],
        editedVerbatim: ov?.editedVerbatim || {},
      };
      setOverlay(normalized);
      setSaved(normalized);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const getText = useCallback(
    (key, def) =>
      Object.prototype.hasOwnProperty.call(overlay.texts, key)
        ? overlay.texts[key]
        : def,
    [overlay.texts],
  );

  const setText = useCallback((key, value, def) => {
    setOverlay((prev) => {
      const texts = { ...prev.texts };
      if (def !== undefined && value === def) delete texts[key];
      else texts[key] = value;
      return { ...prev, texts };
    });
    setDirty(true);
  }, []);

  const isHidden = useCallback(
    (id) => overlay.hiddenVerbatim.includes(String(id)),
    [overlay.hiddenVerbatim],
  );

  const toggleHidden = useCallback((id) => {
    const sid = String(id);
    setOverlay((prev) => {
      const set = new Set(prev.hiddenVerbatim.map(String));
      if (set.has(sid)) set.delete(sid);
      else set.add(sid);
      return { ...prev, hiddenVerbatim: [...set] };
    });
    setDirty(true);
  }, []);

  const getVerbatim = useCallback(
    (id, def) => {
      const sid = String(id);
      return Object.prototype.hasOwnProperty.call(overlay.editedVerbatim, sid)
        ? overlay.editedVerbatim[sid]
        : def;
    },
    [overlay.editedVerbatim],
  );

  const setVerbatim = useCallback((id, value, def) => {
    const sid = String(id);
    setOverlay((prev) => {
      const editedVerbatim = { ...prev.editedVerbatim };
      if (def !== undefined && value === def) delete editedVerbatim[sid];
      else editedVerbatim[sid] = value;
      return { ...prev, editedVerbatim };
    });
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    if (!groupId) return false;
    setSaving(true);
    const res = await saveReportOverride(groupId, overlay);
    setSaving(false);
    if (res) {
      setSaved(overlay);
      setDirty(false);
      return true;
    }
    return false;
  }, [groupId, overlay]);

  const discard = useCallback(() => {
    setOverlay(saved);
    setDirty(false);
  }, [saved]);

  const value = useMemo(
    () => ({
      canEdit: !!canEdit,
      editing,
      setEditing,
      loaded,
      dirty,
      saving,
      overlay,
      getText,
      setText,
      isHidden,
      toggleHidden,
      getVerbatim,
      setVerbatim,
      save,
      discard,
    }),
    [
      canEdit,
      editing,
      loaded,
      dirty,
      saving,
      overlay,
      getText,
      setText,
      isHidden,
      toggleHidden,
      getVerbatim,
      setVerbatim,
      save,
      discard,
    ],
  );

  return value;
};

// Thin wrapper for cases that just need to provide context (no PDF access).
export const ReportEditProvider = ({ groupId, canEdit, children }) => {
  const value = useReportEditValue(groupId, canEdit);
  return (
    <ReportEditContext.Provider value={value}>
      {children}
    </ReportEditContext.Provider>
  );
};
